// Holographic 3D padlock for the password vault (templates/password.liquid). A small canvas renderer:
// a beveled rounded body, a tube shackle and a keyhole are built as a mesh, rotated, back-face culled,
// depth-sorted and lit (diffuse + specular), then drawn translucent with faint mesh lines and scanlines.
// Color follows the element's CSS `color`, so the red "Access denied" state and its fade back work via CSS.
// Reduced motion: one still frame.
const LIGHT = normalize([-0.45, 0.6, 0.75]);
const HALF = normalize([LIGHT[0], LIGHT[1], LIGHT[2] + 1]);

function normalize([x, y, z]) {
  const length = Math.hypot(x, y, z) || 1;
  return [x / length, y / length, z / length];
}

function cross(a, b) {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function buildMesh() {
  const vertices = [];
  const faces = [];
  const add = (point) => vertices.push(point) - 1;

  // Body: rounded-rectangle profile, extruded with a bevel on front and back.
  const W = 0.78, H = 0.6, CY = -0.36, R = 0.2, D = 0.3, BEVEL = 0.07, STEPS = 6;
  const profile = [];
  const corners = [[W - R, H - R, 0], [-W + R, H - R, 90], [-W + R, -H + R, 180], [W - R, -H + R, 270]];
  for (const [cx, cy, start] of corners) {
    for (let s = 0; s <= STEPS; s += 1) {
      const angle = ((start + (90 * s) / STEPS) * Math.PI) / 180;
      profile.push([cx + R * Math.cos(angle), cy + R * Math.sin(angle)]);
    }
  }
  const ring = (scale, z) => profile.map(([x, y]) => add([x * scale, (y * scale) + CY, z]));
  const inset = 1 - BEVEL / W;
  const frontCap = ring(inset, D);
  const frontEdge = ring(1, D - BEVEL);
  const backEdge = ring(1, -D + BEVEL);
  const backCap = ring(inset, -D);
  const n = profile.length;
  const band = (a, b) => {
    for (let i = 0; i < n; i += 1) {
      const j = (i + 1) % n;
      faces.push({ idx: [a[i], a[j], b[j], b[i]], part: 'body' });
    }
  };
  band(frontEdge, frontCap);
  band(backEdge, frontEdge);
  band(backCap, backEdge);
  faces.push({ idx: [...frontCap], part: 'front' });
  faces.push({ idx: [...backCap].reverse(), part: 'body' });

  // Shackle: a tube swept along leg - arc - leg in the xy plane.
  const path = [];
  const LEG_X = 0.5, LEG_BOTTOM = 0.05, ARC_Y = 0.42, TUBE = 0.095, AROUND = 14;
  for (let s = 0; s <= 4; s += 1) path.push([-LEG_X, LEG_BOTTOM + ((ARC_Y - LEG_BOTTOM) * s) / 4]);
  for (let s = 1; s < 18; s += 1) {
    const angle = Math.PI - (Math.PI * s) / 18;
    path.push([LEG_X * Math.cos(angle), ARC_Y + LEG_X * Math.sin(angle)]);
  }
  for (let s = 0; s <= 4; s += 1) path.push([LEG_X, ARC_Y - ((ARC_Y - LEG_BOTTOM) * s) / 4]);
  const rings = path.map((point, i) => {
    const prev = path[Math.max(0, i - 1)];
    const next = path[Math.min(path.length - 1, i + 1)];
    const t = normalize([next[0] - prev[0], next[1] - prev[1], 0]);
    const side = [-t[1], t[0], 0];
    const indices = [];
    for (let k = 0; k < AROUND; k += 1) {
      const a = (k / AROUND) * Math.PI * 2;
      indices.push(add([
        point[0] + TUBE * Math.cos(a) * side[0],
        point[1] + TUBE * Math.cos(a) * side[1],
        TUBE * Math.sin(a),
      ]));
    }
    return indices;
  });
  for (let i = 0; i < rings.length - 1; i += 1) {
    for (let k = 0; k < AROUND; k += 1) {
      const m = (k + 1) % AROUND;
      faces.push({ idx: [rings[i][k], rings[i][m], rings[i + 1][m], rings[i + 1][k]], part: 'shackle' });
    }
  }

  // Keyhole on the front face: circle + slot, just proud of the surface.
  const keyhole = [];
  const KZ = D + 0.003, KY = CY + 0.06;
  for (let s = 0; s <= 20; s += 1) {
    const angle = -Math.PI / 2 + 0.55 + ((Math.PI * 2 - 1.1) * s) / 20;
    keyhole.push(add([0.095 * Math.cos(angle), KY + 0.095 * Math.sin(angle), KZ]));
  }
  keyhole.push(add([-0.045, KY - 0.3, KZ]), add([0.045, KY - 0.3, KZ]));
  faces.push({ idx: keyhole, part: 'keyhole' });

  for (const face of faces) {
    const [a, b, c] = face.idx.map((i) => vertices[i]);
    face.normal = normalize(cross([b[0] - a[0], b[1] - a[1], b[2] - a[2]], [c[0] - a[0], c[1] - a[1], c[2] - a[2]]));
  }
  return { vertices, faces };
}

class SHoloLock extends HTMLElement {
  connectedCallback() {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'block size-full';
    this.canvas.setAttribute('aria-hidden', 'true');
    this.replaceChildren(this.canvas);
    this.ctx = this.canvas.getContext('2d');
    this.mesh = buildMesh();
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this);
    this.intersection = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible && !this.reduced) this.loop();
    });
    this.intersection.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.intersection?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  resize() {
    const { width, height } = this.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.max(1, Math.floor(width * ratio));
    this.canvas.height = Math.max(1, Math.floor(height * ratio));
    this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    this.size = Math.min(width, height);
    this.width = width;
    this.height = height;
    this.draw(this.reduced ? 2000 : performance.now());
  }

  loop() {
    cancelAnimationFrame(this.frame);
    const tick = (time) => {
      if (!this.visible) return;
      this.draw(time);
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  color() {
    const match = getComputedStyle(this).color.match(/[\d.]+/g) || ['0', '255', '65'];
    return match.slice(0, 3).map(Number);
  }

  draw(time) {
    const { ctx, width, height } = this;
    if (!width || !height) return;
    ctx.clearRect(0, 0, width, height);
    const [r, g, b] = this.color();
    const yaw = Math.sin(time * 0.00045) * 0.85;
    const pitch = 0.18 + Math.sin(time * 0.00031) * 0.1;
    const cosY = Math.cos(yaw), sinY = Math.sin(yaw), cosP = Math.cos(pitch), sinP = Math.sin(pitch);
    const rotate = ([x, y, z]) => {
      const x1 = x * cosY + z * sinY;
      const z1 = -x * sinY + z * cosY;
      return [x1, y * cosP - z1 * sinP, y * sinP + z1 * cosP];
    };
    const CAMERA = 4.2;
    const scale = (this.size / 2) / 1.15;
    const cx = width / 2, cy = height / 2 + scale * 0.04;
    const rotated = this.mesh.vertices.map(rotate);
    const projected = rotated.map(([x, y, z]) => {
      const f = CAMERA / (CAMERA - z);
      return [cx + x * f * scale, cy - y * f * scale];
    });

    const visible = [];
    for (const face of this.mesh.faces) {
      const pts = face.idx.map((i) => projected[i]);
      let area = 0;
      for (let i = 0; i < pts.length; i += 1) {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[(i + 1) % pts.length];
        area += x1 * y2 - x2 * y1;
      }
      if (area >= 0) continue;
      const depth = face.idx.reduce((sum, i) => sum + rotated[i][2], 0) / face.idx.length;
      visible.push({ face, pts, depth });
    }
    visible.sort((a, b) => a.depth - b.depth);

    for (const { face, pts } of visible) {
      const normal = rotate(face.normal);
      const diffuse = Math.max(0, normal[0] * LIGHT[0] + normal[1] * LIGHT[1] + normal[2] * LIGHT[2]);
      const spec = Math.max(0, normal[0] * HALF[0] + normal[1] * HALF[1] + normal[2] * HALF[2]) ** 28;
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.closePath();
      if (face.part === 'keyhole') {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.82)';
        ctx.fill();
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.9)`;
        ctx.lineWidth = 1.2;
        ctx.stroke();
        continue;
      }
      const shade = 0.18 + diffuse * 0.82;
      const glint = spec * (face.part === 'shackle' ? 220 : 150);
      const alpha = face.part === 'front' ? 0.62 : 0.78;
      ctx.fillStyle = `rgba(${Math.min(255, r * shade + glint)}, ${Math.min(255, g * shade + glint)}, ${Math.min(255, b * shade + glint)}, ${alpha})`;
      ctx.fill();
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${face.part === 'front' ? 0.75 : 0.22})`;
      ctx.lineWidth = face.part === 'front' ? 1.4 : 0.6;
      ctx.stroke();
    }

    // Holographic scanlines, cut only into the drawn lock.
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
    const offset = this.reduced ? 0 : Math.floor(time / 60) % 4;
    for (let y = offset; y < height; y += 4) ctx.fillRect(0, y, width, 1.5);
    ctx.restore();
  }
}

if (!customElements.get('s-holo-lock')) customElements.define('s-holo-lock', SHoloLock);
